"use client";

import "../../../instrumentation-client";
import posthog from "posthog-js";
import { forwardAdSignal } from "@/lib/acquisition/ad-signals";
import { readClientAcquisition } from "@/lib/acquisition/client";
import { acquisitionProperties } from "@/lib/acquisition/shared";
import {
  buildTelemetryContext,
  isClientTelemetryDeliveryEnabled,
} from "@/lib/telemetry/environment";
import {
  buildErrorRuntimeContext,
  buildRuntimeContext,
} from "@/lib/telemetry/runtime";
import { version as appVersion } from "../../../package.json";
import type { AnalyticsEventName, AnalyticsProperties } from "./events";
import { ANALYTICS_EVENTS } from "./events";

type PostHogFlushControls = typeof posthog & {
  flush?: () => void;
  flushLogs?: (transport?: "XHR" | "fetch" | "sendBeacon") => void;
};

type ClientMetricAttributes = Record<string, string | number | boolean>;

/**
 * The deployment identity, re-registrable after a reset.
 *
 * `posthog.reset()` clears every registered super property, so anything that
 * calls it mid-document silently strips `deployment_environment` and `release`
 * off every subsequent event. Exported so the one caller that legitimately
 * resets can put them straight back.
 */
export function registerTelemetryContext() {
  if (!isClientTelemetryDeliveryEnabled()) return;
  try {
    posthog.register({
      ...buildTelemetryContext(appVersion),
      ...buildRuntimeContext(),
    });
  } catch {
    // Registration is best-effort; a missing global must not break sign-out.
  }
}

/**
 * Stamped on every event rather than relied upon from `posthog.register()`.
 *
 * About a quarter of production events arrived with `app_surface`, `release`
 * and `deployment_environment` all missing together. The cause was
 * `posthog.reset()` running on every anonymous page load and wiping the
 * registered properties — fixed in `posthog-provider.tsx` — but a super
 * property is process-wide state that any future caller can clear again, and
 * these four values are cheap enough to compute per capture that they should
 * not depend on it.
 *
 * Explicit properties still win: a caller that knows better than the probe
 * overrides it.
 */
function withRuntimeContext(
  properties?: AnalyticsProperties,
): AnalyticsProperties {
  try {
    return {
      ...buildTelemetryContext(appVersion),
      ...buildRuntimeContext(),
      ...acquisitionProperties(readClientAcquisition()),
      ...properties,
    };
  } catch {
    // Never let a missing global stop an event from being recorded.
    return properties ?? {};
  }
}

export function captureClientEvent(
  event: AnalyticsEventName,
  properties?: AnalyticsProperties,
) {
  if (!isClientTelemetryDeliveryEnabled()) return;
  posthog.capture(event, withRuntimeContext(properties));
  forwardAdSignal(event, properties);
}

/**
 * Captures an event whose reporting depends on a stable signed-in identity.
 * Identifying immediately avoids a race with the provider's async session
 * lookup, while the explicit property keeps HogQL reports deterministic for
 * events captured before or during an alias transition.
 */
export function captureIdentifiedClientEvent(
  event: AnalyticsEventName,
  userId: string | null | undefined,
  properties?: AnalyticsProperties,
) {
  if (!isClientTelemetryDeliveryEnabled()) return;
  forwardAdSignal(event, properties);
  if (userId) {
    if (posthog.get_distinct_id() !== userId) {
      posthog.identify(userId);
    }
    posthog.capture(event, {
      ...withRuntimeContext(properties),
      viewer_is_authenticated: true,
      viewer_user_id: userId,
    });
    return;
  }

  posthog.capture(event, {
    ...withRuntimeContext(properties),
    viewer_is_authenticated: false,
  });
}

export function captureClientException(
  error: unknown,
  properties?: AnalyticsProperties,
) {
  if (!isClientTelemetryDeliveryEnabled()) return undefined;
  try {
    return posthog.captureException(error, {
      ...buildErrorRuntimeContext(),
      ...properties,
    });
  } catch {
    // Error reporting must never throw from an error boundary.
    return undefined;
  }
}

export function captureClientHistogram(
  metric: string,
  value: number,
  unit: string,
  attributes?: ClientMetricAttributes,
) {
  if (!isClientTelemetryDeliveryEnabled()) return;
  try {
    posthog.metrics.histogram(metric, value, { unit, attributes });
  } catch {
    // Metrics are alpha and intentionally secondary to durable event capture.
  }
}

/**
 * Manual pageview for the Capacitor WebView, where no document load ever
 * happens and posthog-js would otherwise record zero pageviews for the
 * majority of real users. posthog-js still fills in `$current_url`,
 * `$pathname` and `$host` itself, so PostHog web analytics works unchanged.
 */
export function capturePageview(properties?: AnalyticsProperties) {
  if (!isClientTelemetryDeliveryEnabled()) return;
  try {
    posthog.capture("$pageview", properties);
  } catch {
    // Never let analytics break navigation.
  }
}

export function capturePageleave(properties?: AnalyticsProperties) {
  if (!isClientTelemetryDeliveryEnabled()) return;
  try {
    posthog.capture("$pageleave", properties);
  } catch {
    // Never let analytics break navigation.
  }
}

/**
 * A server action that returned `ok: false` is a product failure the user
 * saw, and it never reaches `onRequestError` because nothing threw. Without
 * this, "checkout silently does nothing" produces no telemetry at all.
 */
export function captureActionFailure(
  action: string,
  message: string | undefined,
  properties?: AnalyticsProperties,
) {
  captureClientEvent(ANALYTICS_EVENTS.SERVER_ACTION_FAILED, {
    action,
    failure_message: message?.slice(0, 300),
    ...properties,
  });
}

export function flushClientTelemetry() {
  if (!isClientTelemetryDeliveryEnabled()) return;
  try {
    const client = posthog as PostHogFlushControls;
    client.flush?.();
    client.flushLogs?.("sendBeacon");
  } catch {
    // Telemetry flushing is best-effort.
  }
}

export function identifyAnalyticsUser(
  distinctId: string,
  properties?: AnalyticsProperties,
) {
  if (!isClientTelemetryDeliveryEnabled()) return;
  const previousUserId = getIdentifiedAnalyticsUserId();

  // `identify(B)` while this browser is still persisted as identified user A
  // is an account-linking operation, not merely a label change. A direct OAuth
  // account switch can replace the app session without rendering the signed-
  // out state in between, so the provider's React ref is not sufficient. Reset
  // first to start a fresh anonymous/session boundary, then identify B. This is
  // the same boundary PostHog recommends for shared-device logout flows.
  if (previousUserId && previousUserId !== distinctId) {
    posthog.reset();
    registerTelemetryContext();
  }

  posthog.identify(distinctId, properties);
}

export function getIdentifiedAnalyticsUserId(): string | null {
  if (!isClientTelemetryDeliveryEnabled()) return null;
  try {
    const value = posthog.get_property("$user_id");
    return typeof value === "string" && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

export function resetAnalyticsUser() {
  if (!isClientTelemetryDeliveryEnabled()) return;
  posthog.reset();
}
